import { GbiCodeResponse } from '@flows/src/types/ApiTypes';
import {
  Box,
  VStack,
  Text,
  Center,
  Checkbox,
  HStack,
} from '@wegroup/design-system';
import { UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce/lib';
import LoadingSpinner from '../../../../../../common/src/components/LoadingSpinner';
import { useGbiCodesQuery } from '../../../../hooks/useFlowsQueries';
import {
  GbiCode,
  GbiCodeSearchResult,
} from '../../../../types/CompanyScanTypes';
import { FormValues } from './GbiCodeModal';

interface Props {
  gbiCodes: GbiCode[];
  onAdd: (gbiCode: GbiCode) => void;
  onRemove: (gbiCode: GbiCode) => void;
  watch: UseFormWatch<FormValues>;
}

const GbiCodeList = ({ gbiCodes, onAdd, onRemove, watch }: Props) => {
  const { t } = useTranslation();

  const searchQuery = watch('searchQuery');

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const { data, isLoading } = useGbiCodesQuery(debouncedSearchQuery);

  const MAX_DISPLAY_RESULTS = 20;
  const displayedItems = getDisplayedItems(data, MAX_DISPLAY_RESULTS);
  const remainingCount = data?.items?.length
    ? data.items.length - MAX_DISPLAY_RESULTS
    : 0;

  const isSelected = (gbiCode: GbiCode) => {
    return gbiCodes.some((g) => g.code === gbiCode.code);
  };

  const handleToggle = (gbiCode: GbiCode) => {
    if (isSelected(gbiCode)) {
      onRemove(gbiCode);
    } else {
      onAdd(gbiCode);
    }
  };

  if (isLoading)
    return (
      <Center p="6">
        <LoadingSpinner />
      </Center>
    );

  if (data?.items?.length === 0)
    return (
      <Center p="6" pt="0">
        <Text textStyle="h4Headline">{t('No results found')}</Text>
      </Center>
    );

  return (
    <Box>
      {displayedItems.map((gbiCode) => (
        <VStack key={gbiCode.code} spacing="2" align="stretch" py="2">
          <Checkbox
            isChecked={isSelected(gbiCode)}
            onChange={() => handleToggle(gbiCode)}
          >
            <HStack>
              <Text fontWeight="bold">{gbiCode.code}</Text> -{' '}
              <Text>{gbiCode.label}</Text>
            </HStack>
          </Checkbox>
        </VStack>
      ))}
      {remainingCount > 0 && (
        <Center py="4">
          <Text color="text.gray" fontStyle="italic">
            {t('AND-X-MORE', {
              count: remainingCount,
            })}
          </Text>
        </Center>
      )}
    </Box>
  );
};

export default GbiCodeList;

function getDisplayedItems(
  data: GbiCodeResponse | undefined,
  max: number,
): GbiCode[] {
  return data?.items?.slice(0, max).map(mapSearchResultToGbiCode) || [];
}
function mapSearchResultToGbiCode(searchResult: GbiCodeSearchResult): GbiCode {
  return {
    code: searchResult.gbi_id,
    label: searchResult.description,
  };
}
